<template>
  <div class="job-edit-wrapper">
    <JobEditor
      v-if="ready"
      isPreview
      v-show="!showPreview"
      class="job-editor"
      :is-edit-mode="isEditMode"
      :value="jobInfo"
      :hiddenSaveBtn="!hasSaveJobPermission"
      @cancel="handleCancel"
      @save="handleSave"
      @preview="handlePreview"
      @save-back="handleSaveAndBack"
      @ai-job-comparison-save-back="handleSaveAiComparisonAndBack"
      @job-fit-save-back="handleSaveAIJobFitCondition"
    />
    <PreviewPage
      v-if="showPreview"
      :jobDetailInfo="jobTemplateInfo"
      @closePreview="handleClosePreview"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import JobEditor from '@/components/job/editor/layout.vue'
import PreviewPage from './previewPage.vue'
import moment from 'moment'

export default {
  components: {
    JobEditor,
    PreviewPage
  },
  data () {
    return {
      ready: false,
      readyPromise: [],
      jobInfo: {
        expire_date: moment().add(30, 'days').startOf('day').toDate()
      },
      jobTemplateInfo: null,
      isShowPreview: false,
      job: {
        id: '',
        root_company_id: '',
        currency: '',
        cities: [],
        experience: '',
        work_time: '',
        cn_description: '',
        description: '',
        is_in_group: '',
        compensation: null,
        role_type: '',
        customized_header_en: null,
        customized_footer_en: null,
        customized_header_cn: null,
        customized_footer_cn: null,
        to_show_referral: '',
        i18n_name: '',
        updated_time: new Date(),
        company: {
          logo: '',
          i18n_name: '',
          id: ''
        }
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    jobId () {
      return this.$route.params.jobId
    },
    showPreview () {
      return this.$route.query.isPreview && this.isShowPreview
    },
    hasAIComparisonPermission () {
      return this.hasAnyPermission('PREMIUM_AI_COMPARISON')
    },
    canViewAIFitScore () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    hasJobEditPermission () {
      return this.hasAnyPermission('PERM_JOB_EDIT')
    },
    hasSaveJobPermission () {
      return !this.isEditMode || this.hasJobEditPermission
    }
  },
  created () {
    if (this.isEditMode) {
      this.readyPromise.push(this.getJobInfo())
    }
    Promise.all(this.readyPromise).then(() => {
      this.ready = true
    })
  },
  methods: {
    ...mapActions('jobManagement', ['getJobInfoById', 'createJob', 'updateJob']),
    ...mapActions('job', ['createJobCriterionByParseJob', 'updateJobCriterion']),
    ...mapActions('interview', ['createOrUpdateJobAIInterviewRoom']),
    ...mapActions('jobFit', ['saveJobFitCondition']),
    async getJobInfo () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.getJobInfoById(this.jobId)
        if (resp) {
          this.jobInfo = resp
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async createJobCriterion () {
      if (!this.hasAIComparisonPermission) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.createJobCriterionByParseJob({ job_id: this.jobId })
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleCancel () {
      mixedRouterPush(this.$route.query.back || '/v-admin/job-list/')
    },
    async handlePreview (jobTemplateInfo) {
      this.jobTemplateInfo = { ...this.job, ...jobTemplateInfo }
      if (!jobTemplateInfo.job) {
        this.jobTemplateInfo.job = this.job
      }
      const query = { ...this.$route.query, isPreview: 1 }
      this.$router.push({ query }).catch(err => err)
      this.isShowPreview = true
      document.documentElement.scrollTop = 0
    },
    handleSaveSuccess (autoBack) {
      this.$message.success(this.$t('common.message.saveSuccess'))
      if (this.$route.query.close_tab && window.opener) {
        window.opener.postMessage({
          hasClosed: true
        }, window.location.origin)
        window.close()
      } else {
        if (autoBack) {
          const query = this.$route.query
          mixedRouterPush(query.back || `/v-admin/job-list/?companyId=${query.company_id || ''}`)
        }
      }
    },
    async handleSave (jobTemplateInfo, autoBack, step, standaloneStep) {
      if (!this.hasSaveJobPermission) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        let saveFunc = this.createJob
        if (this.isEditMode) {
          saveFunc = this.updateJob
        }
        const resp = await saveFunc(jobTemplateInfo)
        if (resp) {
          if (jobTemplateInfo.interview_room_id) {
            await this.createOrUpdateJobAIInterviewRoom({
              job_id: resp.id,
              interview_room_id: jobTemplateInfo.interview_room_id
            })
          }
          if (standaloneStep) {
            this.$message.success(this.$t('common.message.saveSuccess'))
            const query = { ...this.$route.query, step, standalone_step: standaloneStep }
            await this.$router.push({
              path: `/v-admin/job/edit/${resp.id}`,
              query: query
            })
            this.ready = false
            this.jobInfo = resp
            await this.$nextTick()
            this.ready = true
          } else {
            this.handleSaveSuccess(autoBack)
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleSaveAndBack (jobTemplateInfo, step, standaloneStep) {
      return this.handleSave(jobTemplateInfo, true, step, standaloneStep)
    },
    async handleSaveAiComparisonAndBack (jobCriterionList, autoBack) {
      if (!this.hasSaveJobPermission || !this.hasAIComparisonPermission) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const params = {
          job_id: this.jobId,
          job_criterion_list: jobCriterionList
        }
        const resp = await this.updateJobCriterion(params)
        if (resp) {
          this.handleSaveSuccess(autoBack)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleSaveAIJobFitCondition (jobFitCondition, autoBack) {
      if (!this.hasSaveJobPermission || !this.canViewAIFitScore) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const params = {
          jobId: this.jobId,
          condition: jobFitCondition
        }
        const resp = await this.saveJobFitCondition(params)
        if (resp) {
          this.handleSaveSuccess(autoBack)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleClosePreview () {
      this.isShowPreview = !this.isShowPreview
    }
  }
}
</script>

<style lang="scss" scoped>
.job-edit-wrapper {
  .job-editor {
    min-width: 1345px;
    overflow: auto;
  }
}
</style>
